export * from './domain/telemetry/raw/GpsReading'
export * from './domain/telemetry/raw/GyroscopeReading'
export * from './domain/telemetry/raw/AccelerometerReading'
export * from './domain/telemetry/raw/RawTelemetryData'

export * from './domain/telemetry/processed/GpsPoint'
export * from './domain/telemetry/processed/LeanAngle'
export * from './domain/telemetry/processed/Acceleration'
export * from './domain/telemetry/processed/GForce'
export * from './domain/telemetry/processed/TelemetryStatistics'
export * from './domain/telemetry/processed/ProcessedTelemetryData'
export * from './domain/telemetry/processed/TelemetryDiagnostics'

export * from './domain/telemetry/indexing/TelemetryIndex'
export * from './domain/telemetry/indexing/IndexedTelemetry'

export * from './domain/telemetry/spatial/SpatialBucket'

export type { GpsPoint as ProcessedGpsPoint } from './domain/telemetry/processed/GpsPoint'

export * from './domain/lap/LapBounds'
export * from './domain/lap/LapMetadata'
export * from './domain/lap/Lap'
export * from './domain/lap/LapPoint'
export * from './domain/lap/lapAnalysis'
export * from './domain/lap/TimeDriftCalculator'

export * from './domain/track-session/TrackSessionMetadata'
export * from './domain/track-session/TrackSessionStatus'
export * from './domain/track-session/TrackSession'

export * from './domain/track/TrackLocation'
export * from './domain/track/FinishLine'
export * from './domain/track/TrackMetadata'

export * from './utils/distanceUtils'
export * from './utils/geometryUtils'
export * from './utils/guidUtils'
export * from './utils/timeUtils'
export * from './utils/timeUnits'
export * from './utils/lengthUnits'
export * from './utils/speedUnits'
export * from './utils/extremes'
export * from './utils/telemetryUtils'
export * from './utils/sustainedPeak'
export * from './utils/errorMessage'

export {
  createMultiResolution,
  decimateTelemetryByDistance,
  createLegacyIndexedTelemetry,
} from './processors/telemetryProcessor'
export * from './processors/chartDataProcessor'
export * from './processors/rawTelemetryProcessor'
export * from './processors/stationaryTrim'
export { applyGpsDerivedTier } from './processors/gpsDerivedTierRelabel'
export {
  classifyImuVibration,
  reduceAccelerometerToGpsRate,
} from './processors/gpsRateAccelReduction'
export * from './processors/lapComparison'
export * from './processors/gpsFixGate'
export * from './processors/pipeline'
export * from './processedSession'
export { createSpatialBuckets } from './processors/spatial/spatialProcessing'
export {
  gpsSpeedTrendMs2,
  validateAccelConsistency,
} from './processors/accelConsistencyValidator'

export { executeCoastingPipeline } from './processors/coasting'
export {
  buildCoastingMap,
  getCoastingAtDistance,
  collectAndClassifyCoastingData,
  calculateCoastingIntensity,
  calculateCombinedGForce,
  DEFAULT_COASTING_CONFIG,
} from './processors/coasting'
export {
  createVehicleStrategy,
  DEFAULT_VEHICLE_TYPE,
  MotorcycleStrategy,
  CarStrategy,
  GoKartStrategy,
} from './processors/coasting'
export type {
  CoastingPeriod,
  CoastingConfig,
  VehicleType,
  VehicleStrategy,
  VehiclePhysicsConfig,
  TransitionTiming,
  TransitionSpeed,
  TransitionAnalysis,
} from './processors/coasting'

export * from './types/TelemetryTypes'
export type { IGpsPointProvider } from './types/GpsPoint'
export * from './types/LatLng'

export type {
  PzRacingDataRow,
  I2mDataRow,
  AimDataRow,
  CsvDataRow,
  Coordinate,
  UpsertLapRequest,
  LapDto,
  Weather,
  CreateTrackSessionRequest,
  DriverDto,
  CreateVehicleRequest,
  VehicleDto,
  VehicleEngineDto,
  TrackSessionDto,
  TrackSessionMetadataDto,
  LatLngDto,
  Speed,
  Device,
  TelemetryDto,
  TelemetryDiagnosticsDto,
  Gps,
  Gyroscope,
  Accelerometer,
  Direction,
  DeviceTime,
  GyroReading,
  AccelerationReading,
  TrackDto,
  UserSuggestedTrack,
  NearbyTrackResult,
  TrackChangeResult,
  Guid,
} from './api/apiTypes'

export {
  TelemetryType,
  UserSuggestedTrackStatus,
  WeatherCondition,
  guid,
} from './api/apiTypes'

export type { TrackDto as Track } from './api/apiTypes'

export { rawTelemetryToDto } from './api/mappers/telemetryMapper'
export { telemetryDtoToRaw } from './api/mappers/telemetryMapper'
export { sessionDiagnosticsToDto } from './api/mappers/telemetryMapper'
export { deviceMetadataOf, UNKNOWN_DEVICE } from './api/mappers/deviceMapper'
export {
  lapToDto,
  lapDtoToLap,
  lapTelemetryToDto,
} from './api/mappers/lapMapper'

export * from './analysis/metrics/TelemetryMetricTypes'

export * from './analysis/visualization/GradientColorScales'

export type {
  GradientColorScale,
  GradientPoint,
} from './analysis/visualization/gradientTypes'

export { VARIANCE_COLOR_SCALE } from './analysis/variance/types'
export {
  extractMetricFromPreprocessed,
  normalizeTimeDrift,
} from './analysis/variance/metricExtractors'
export {
  computeAutoExcludedLapIds,
  type OutlierLapInput,
} from './analysis/sessionAnalysis/lapOutlierExclusion'
export {
  buildLapTimeDeltas,
  type LapTimeDelta,
} from './analysis/sessionAnalysis/lapTimeDeltas'
export {
  getKeyLapIds,
  getDisplayLaps,
  getLapSortValue,
} from './analysis/sessionAnalysis/keyLapSelection'
export {
  getZoneMarkerKind,
  type ZoneMarkerKind,
} from './analysis/sessionAnalysis/zoneMarkerKind'
export {
  runSessionAnalysis,
  sessionAnalysisResult,
  filterExcludedLaps,
  detectSessionZones,
  type SessionAnalysisOptions,
  type SessionAnalysisResult,
} from './analysis/sessionAnalysis/analyzeSession'
export { selectStoredLaps } from './analysis/sessionAnalysis/lapSelection'
export {
  defaultSessionView,
  type DefaultSessionView,
} from './analysis/sessionAnalysis/defaultSessionView'

export * from './analysis/config'

export {
  buildZoneChartSeries,
  zoneSeriesGatedLapIds,
  zoneSeriesGatedLapReasons,
  zoneChartMetric,
  zoneChartMetricOptions,
  fitSpeedAxisBounds,
  ZONE_CHART_METRIC,
  ZONE_CHART_METRIC_LABELS,
  BRAKE_METRIC_CAPTION,
  CHART_GUIDE_TITLE,
  CHART_MARKER_CAPTION,
  type ZoneChartMetric,
  type ZoneChartPoint,
  type ZoneChartSeries,
  type BuildZoneChartSeriesOptions,
} from './analysis/telemetryChart/zoneChartSeries'
export {
  buildMedianSpeedReference,
  speedDeltaAt,
  speedDeltaAxisBound,
  SPEED_DELTA_AXIS_LABEL,
  SPEED_DELTA_ZERO_LABEL,
  SPEED_DELTA_CAPTION,
  SPEED_DELTA_GUIDE_KMH,
  SPEED_DELTA_GUIDE_LABELS,
  SPEED_DELTA_GUIDE_CAPTION,
  type SpeedSample,
  type SpeedReference,
} from './analysis/telemetryChart/speedDeltaSeries'
export {
  buildSignedAccelDisplay,
  buildSignedAccelReference,
  maxAccelGAtSpeed,
  lapGateReason,
  describeGatedLaps,
  SIGNED_ACCEL_SPEED_BIN_KMH,
  SIGNED_BRAKE_G_CAP,
  SIGNED_ACCEL_G_CAP,
  SIGNED_MIN_G_REFERENCE,
  ACCEL_BAND_FRACTION,
  SIGNED_ACCEL_FILL_MAX_LAPS,
  type SignedAccelSourcePoint,
  type SignedAccelLapInput,
  type SignedAccelReference,
  type SignedAccelGateReason,
  type SignedAccelPoint,
  type SignedAccelLap,
  type SignedAccelDisplay,
} from './analysis/telemetryChart/signedAccelDisplay'

export {
  refineZoneBoundaries,
  findTurnInDistance,
  findCorneringDisplayStart,
  computeZoneDisplayBounds,
} from './analysis/sessionAnalysis/zoneDisplay/zoneDisplayBounds'
export {
  buildRecalibratedLapPoints,
  type RecalibratedLap,
} from './analysis/sessionAnalysis/zoneDisplay/recalibratedLapPoints'
export {
  speedToleranceKmh,
  distanceToleranceM,
  type ConsistencyDisplayBounds,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/consistencyThresholds'
export {
  buildPercentRows,
  composeZoneConsistencyPercent,
  focusConsistentMessage,
  pctBand,
  zonePctBand,
  formatPercentMarkerLabel,
  timeToleranceMs,
  type PercentMetricKey,
  type PercentBand,
  type PercentRowNature,
  type ConsistencyPercentRow,
  type PercentOutlier,
  type PercentOffLap,
  type PercentRowStats,
  type PercentMarker,
  type ZoneConsistencyPercent,
  type ZoneOutlierAttribution,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/consistencyPercent'
export {
  buildCurvePercentRow,
  buildZoneConsistencyPercent,
  type BuildCurvePercentRowArgs,
  type BuildZoneConsistencyPercentArgs,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/curvePercent'
export { binMedianSpeeds } from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/binSpeedSpreads'
export {
  tracesFromRecalibratedLaps,
  tracesFromRawLaps,
  tracesFromChartTelemetry,
  type SpeedTrace,
  type SpeedTracePoint,
  type ChartTelemetryLapLike,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/speedTraces'
export {
  FLAT_VALLEY_MIN_WIDTH_M,
  computeValleyWidthM,
  flatValleyNote,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/flatValley'
export { formatZoneTimeDelta } from './analysis/sessionAnalysis/zoneDisplay/timeDelta'
export { lapTableFootnote } from './analysis/sessionAnalysis/zoneDisplay/lapTableFootnote'
export {
  fastestZoneLapId,
  slowestZoneLapId,
} from './analysis/sessionAnalysis/zoneDisplay/lapExtremes'
export {
  buildConsistencyBand,
  CONSISTENCY_BAND_CAPTION,
  type ConsistencyBand,
  type BuildConsistencyBandArgs,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/consistencyBand'
export {
  buildConsistencyGradient,
  type BuildConsistencyGradientArgs,
  type ZoneVariancePoint,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/consistencyGradient'
export {
  buildTrackConsistencyGradient,
  type TrackGradientZoneWindow,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/trackConsistencyGradient'

export { summarizeLapCollection } from './analysis/lapCollectionSummary'
export type { LapCollectionSummary } from './analysis/lapCollectionSummary'

export {
  getWeatherLabel,
  getWeatherColor,
  toWeatherCondition,
} from './domain/weather/weatherFormatting'

export {
  getFinishLinePosition,
  areTracksCompatible,
} from './domain/track/trackUtils'

export {
  analyzeTrackZones,
  sessionConsistencyPercent,
  sessionConsistencySummary,
  isSolidZone,
  identifyFocusZones,
  identifyImproveZones,
} from './analysis/zones/TrackZoneAnalysis'
export type { SessionConsistencySummary } from './analysis/zones/TrackZoneAnalysis'
export {
  detectZoneBoundaries,
  extractZoneData,
} from './analysis/zones/ZoneDetection'
export { G_CONVERSION } from './analysis/zones/LapZoneMetrics'
export { hasReliableBrakeRelease } from './analysis/zones/ZoneLapData'
export type {
  ZoneBoundary,
  ZoneDataPoint,
} from './analysis/zones/ZoneDetection'
export type {
  TrackZone,
  TrackZoneType,
  ZoneAnalysisResult,
  ZoneLapData,
  ZoneConsensusMetrics,
  ZoneVarianceMetrics,
  ZoneImpact,
  ZoneDetectionConfig,
  ZoneSeverity,
  InconsistencyType,
} from './analysis/zones/TrackZoneTypes'

export type {
  GpmfPayload,
  GpmfGpsSample,
  GpmfXyzSample,
  GpmfStreams,
} from './devices/gopro'
export { parseGpmfPayloads, parseGpmfBuffer, gps9TimeMs } from './devices/gopro'

export { parseGpx } from './devices/gpx'
